import { useEffect, useRef, useState } from "react";

const VIDEO_SRC =
  "https://public.readdy.ai/ai/video_res/019ecf30-3ca3-7ac1-84df-2ff4e2dccc69.mp4";

// How many pixels of horizontal cursor movement = one full rotation loop.
const PX_PER_FULL_LOOP = 1500;

type VideoWithFrameCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (
    cb: (now: number, meta: { mediaTime: number }) => void,
  ) => number;
  disableRemotePlayback?: boolean;
};

export default function Hero() {
  const videoRef = useRef<HTMLVideoElement>(null);

  // Cursor-driven target time (where the cursor says the video should be).
  // We update this on every mousemove and apply it inside rVFC so we only
  // ever issue ONE pending seek at a time — that's the trick to silky scrub.
  const targetTime = useRef(0);
  const lastAppliedTime = useRef(-1);
  const lastMouseX = useRef<number | null>(null);
  const durationRef = useRef(0);
  const isSeeking = useRef(false);
  const hasInteracted = useRef(false);

  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const video = videoRef.current as VideoWithFrameCallback | null;
    if (!video) return;

    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;
    video.disablePictureInPicture = true;
    video.disableRemotePlayback = true;

    const onLoadedMetadata = () => {
      durationRef.current = video.duration;
      targetTime.current = 0;
      try {
        video.currentTime = 0;
      } catch {
        /* noop */
      }
    };

    const onCanPlay = () => {
      setIsReady(true);
    };

    const onSeeked = () => {
      isSeeking.current = false;
      // If the user kept moving while we were seeking, apply the latest target.
      maybeApplyTarget();
    };

    const maybeApplyTarget = () => {
      if (isSeeking.current) return;
      const dur = durationRef.current;
      if (dur <= 0) return;
      const t = targetTime.current;
      // Only seek if the target is meaningfully different — avoids redundant
      // currentTime writes which were causing visible jitter.
      if (Math.abs(t - lastAppliedTime.current) < 0.008) return;
      lastAppliedTime.current = t;
      isSeeking.current = true;
      try {
        video.currentTime = t;
      } catch {
        isSeeking.current = false;
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      const dur = durationRef.current;
      if (dur <= 0) return;
      const clientX = e.clientX;
      if (lastMouseX.current === null) {
        lastMouseX.current = clientX;
        return;
      }
      const deltaX = clientX - lastMouseX.current;
      lastMouseX.current = clientX;
      if (deltaX === 0) return;

      hasInteracted.current = true;

      // Right-half boost: when the cursor sits on the right side of the
      // viewport, scrub a bit faster so it feels just as responsive as
      // the left side. Ramps from 1.0× at center to ~1.6× at the right edge.
      const vw = window.innerWidth || 1;
      const normalized = Math.max(0, Math.min(1, clientX / vw));
      // 0 → 0.5  : factor stays 1.0 (left half unchanged)
      // 0.5 → 1  : factor ramps from 1.0 up to 1.6
      const rightBoost =
        normalized <= 0.5 ? 1 : 1 + (normalized - 0.5) * 2 * 0.6;

      // 1:1 cursor → time mapping. PX_PER_FULL_LOOP px of cursor movement
      // equals one full video duration (faster on the right half).
      const dt = ((deltaX * rightBoost) / PX_PER_FULL_LOOP) * dur;
      let next = targetTime.current + dt;
      // Clamp to [0, duration] — no wrap so it feels like a direct scrub.
      if (next < 0) next = 0;
      if (next > dur - 0.001) next = dur - 0.001;
      targetTime.current = next;

      maybeApplyTarget();
    };

    video.addEventListener("loadedmetadata", onLoadedMetadata);
    video.addEventListener("canplay", onCanPlay);
    video.addEventListener("seeked", onSeeked);
    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
      video.removeEventListener("canplay", onCanPlay);
      video.removeEventListener("seeked", onSeeked);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <section
      id="top"
      className="relative w-full h-screen min-h-[760px] overflow-hidden bg-background-50"
    >
      {/* The video itself IS the visual — native resolution, no canvas downscale */}
      <video
        ref={videoRef}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
          isReady ? "opacity-100" : "opacity-0"
        }`}
        src={VIDEO_SRC}
        muted
        playsInline
        preload="auto"
        crossOrigin="anonymous"
        aria-hidden="true"
      />

      {/* Subtle dark overlay for text contrast */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/30 pointer-events-none"></div>

      {/* TEXT OVERLAY — all text in white */}
      <div className="relative z-10 w-full h-full px-6 md:px-10 lg:px-16 pt-28 pb-10 flex flex-col text-white pointer-events-none">
        {/* TOP ROW — massive headline TOP-RIGHT in white */}
        <div className="flex justify-end">
          <h1
            className="text-right font-black uppercase leading-[0.85] tracking-[-0.02em] text-white"
            style={{
              fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
              fontSize: "clamp(3rem, 8vw, 7.5rem)",
              textShadow: "0 2px 24px rgba(0,0,0,0.18)",
            }}
          >
            Fearless
            <br />
            vision
            <br />
            delivered
          </h1>
        </div>

        {/* MIDDLE ROW — left manifesto / right CTA link */}
        <div className="flex-1 flex items-center justify-between">
          <div className="max-w-[220px] text-[11px] tracking-[0.22em] uppercase leading-relaxed text-white/90">
            Shaping bold
            <br />
            visions into power
            <br />
            in your tribe
          </div>

          <a
            href="#contact"
            className="pointer-events-auto inline-flex items-center gap-2 text-sm md:text-base font-semibold tracking-[0.18em] uppercase text-white hover:text-white/80 transition-colors whitespace-nowrap cursor-pointer"
          >
            Work with us
            <i className="ri-arrow-right-up-line text-lg"></i>
          </a>
        </div>

        {/* BOTTOM ROW — left small caption / right stats blocks */}
        <div className="flex items-end justify-between gap-10">
          <div className="text-[11px] tracking-[0.22em] uppercase leading-relaxed text-white/90 max-w-[280px]">
            Creative studios built
            <br />
            around elevating your vision
            <br />
            into striking reality
          </div>

          <div className="flex items-start gap-10 md:gap-14">
            <div className="text-left">
              <div
                className="text-3xl md:text-4xl font-semibold leading-none tracking-tight text-white"
                style={{ fontFamily: '"Inter", "Helvetica Neue", sans-serif' }}
              >
                +300
              </div>
              <div className="mt-2 text-[10px] tracking-[0.25em] uppercase text-white/85">
                Crafted
                <br />
                brands
              </div>
            </div>
            <div className="text-left">
              <div
                className="text-3xl md:text-4xl font-semibold leading-none tracking-tight text-white"
                style={{ fontFamily: '"Inter", "Helvetica Neue", sans-serif' }}
              >
                +200
              </div>
              <div className="mt-2 text-[10px] tracking-[0.25em] uppercase text-white/85">
                Digital
                <br />
                products
              </div>
            </div>
            <div className="text-left">
              <div
                className="text-3xl md:text-4xl font-semibold leading-none tracking-tight text-white"
                style={{ fontFamily: '"Inter", "Helvetica Neue", sans-serif' }}
              >
                +100
              </div>
              <div className="mt-2 text-[10px] tracking-[0.25em] uppercase text-white/85">
                Ventures
                <br />
                funded
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}